define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../System/Vec3Obj',
    '../System/ColorObj'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    Vec3Obj,
    ColorObj
) {
    'use strict';

    /**
     * 视频融合。
     * <br /><br />
     * <div align='center'>
     * <img src='images/VideoFusion.gif' width='900' height='650' /><br />
     * 视频融合效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/VideoFusion.html|CooWasm VideoFusion demo}
     * @alias VideoFusion
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {String} props.serverUrl websocket服务地址。
     * @param {String} props.videoUrl 视频流地址。
     * @param {EnumVideoPlayMode} [props.playMode=EnumVideoPlayMode.AUTO] 播放模式，当为AUTO时，playStatus不生效。
     * @param {Number} [props.autoPlayDistance=10] 最小自动播放距离。
     * @param {EnumVideoPlayStatus} [props.playStatus=EnumVideoPlayStatus.PLAY] 视频播放状态，当playMode为AUTO时不生效。
     * @param {Vec3Obj} props.eye 观察点坐标。
     * @param {Vec3Obj} props.center 目标点坐标。
     * @param {Vec3Obj} props.up 上方向。
     * @param {Number} props.fovy 视野角度。
     * @param {Number} props.aspect 纵横比。
     * @param {Number} [props.nearDistance=1.0] 近裁剪面距离。
     * @param {Number} [props.farDistance=80.0] 远裁剪面距离。
     * @param {Number} [props.fadeOutDuration=3.0] 淡出持续时间。
     * @param {Number} [props.edgeTransparency=0.05] 边缘透明系数，范围0-0.5。
     * @param {ColorObj} [props.borderLineColor=new ColorObj(0.0, 1.0, 0.0, 1.0)] 轮廓线颜色。
     * @param {Boolean} [props.bBorderLine=false] 是否开启轮廓线。
     * @param {Number} [props.renderDistance=50.0] 绘制距离。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {Number} [props.minTransparencyDistortion=0.4] 最小透明度失真。
     * @param {Boolean} [props.bBase64=false] 是否为Base64编解码。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * 
     */
    class VideoFusion {
        /**
         * @description websocket服务地址。
         * @type {String}
         */
        serverUrl
        /**
         * @description 视频流地址。
         * @type {String}
         */
        videoUrl
        /**
         * @description 播放模式，当为AUTO时，playStatus不生效。
         * @type {EnumVideoPlayMode}
         */
        playMode
        /**
         * @description 最小自动播放距离。
         * @type {Number}
         */
        autoPlayDistance
        /**
         * @description 视频播放状态，当playMode为AUTO时不生效。
         * @type {EnumVideoPlayStatus}
         */
        playStatus
        /**
         * @description 观察点坐标。
         * @type {Vec3Obj}
         */
        eye
        /**
         * @description 目标点坐标。
         * @type {Vec3Obj}
         */
        center
        /**
         * @description 上方向。
         * @type {Vec3Obj}
         */
        up
        /**
         * @description 视野角度。
         * @type {Number}
         */
        fovy
        /**
         * @description 纵横比。
         * @type {Number}
         */
        aspect
        /**
        * @description 近裁剪面距离。
        * @type {Number}
        */
        nearDistance
        /**
        * @description 远裁剪面距离。
        * @type {Number}
        */
        farDistance
        /**
        * @description 淡出持续时间。
        * @type {Number}
        */
        fadeOutDuration
        /**
        * @description 边缘透明系数，范围0-0.5。
        * @type {Number}
        */
        edgeTransparency
        /**
        * @description 轮廓线颜色。
        * @type {ColorObj}
        */
        borderLineColor
        /**
        * @description 是否开启轮廓线。
        * @type {Boolean}
        */
        bBorderLine
        /**
        * @description 绘制距离。
        * @type {Number}
        */
        renderDistance
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
        * @description 最小透明度失真。
        * @type {Number}
        */
        minTransparencyDistortion
        /**
         * @description 是否为Base64编解码。
         * @type {Boolean}
         * @default false
         */
        bBase64 = false
        /**
         * @description 视频显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true
        /**
         * @description 视频对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 视频渲染组件uuid。
         * @type {String}
         * @readonly
         */
        componentRenderUuid = null
        /**
         * @description 视频取流组件uuid。
         * @type {String}
         * @readonly
         */
        componentStreamUuid = null
        /**
         * @description 视频解码组件uuid。
         * @type {String}
         * @readonly
         */
        componentDecodeUuid = null
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description VideoFusion在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "VideoFusion"
        static avatarType = "deSMP.Avatar.MonitorAvatar"
        static componentRenderType = "AvatarComponent.MonitorRenderComponent.FrustumMonitorRenderComponent"
        static componentStreamType = "AvatarComponent.ImageStreamComponent.WebsocketComponent"
        static componentDecodeType = "AvatarComponent.VideoDecodeComponent.FfmpegSoftDecodeComponent" 
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            if (!defined(props.serverUrl)) {
                console.log("need server url!")
                return null
            }
            if (!defined(props.videoUrl)) {
                console.log("need video url!")
                return null
            }
            if (!defined(props.eye)) {
                console.log("need eye!")
                return null
            }
            if (!defined(props.center)) {
                console.log("need center!")
                return null
            }
            if (!defined(props.up)) {
                console.log("need up!")
                return null
            }
            if (!defined(props.fovy)) {
                console.log("need fovy!")
                return null
            }
            if (!defined(props.aspect)) {
                console.log("need aspect!")
                return null
            }
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.MonitorAvatar"
            let renderCategoryName = "AvatarComponent.MonitorRenderComponent.FrustumMonitorRenderComponent"
            let streamCategoryName = "AvatarComponent.ImageStreamComponent.WebsocketComponent"
            let decodeCategoryName = "AvatarComponent.VideoDecodeComponent.FfmpegSoftDecodeComponent"
            let avcCategoryName = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
            let categoryNames = ""
            let avatarProps = ""
            let componetProps = ""
            //avatar props
            avatarProps += this.toStr("playMode", props.playMode, "PlayMode")
            avatarProps += this.toStr("autoPlayDistance", props.autoPlayDistance, "AutoPlayDistance")
            avatarProps += this.toStr("playStatus", props.playStatus, "ControlPlayStatus")
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
            //render componet props
            let renderProps = ""
            renderProps += this.toStr("eye", props.eye, "Eye")
            renderProps += this.toStr("center", props.center, "Center")
            renderProps += this.toStr("up", props.up, "Up")
            renderProps += this.toStr("fovy", props.fovy, "Fovy")
            renderProps += this.toStr("aspect", props.aspect, "Aspect")
            renderProps += this.toStr("nearDistance", props.nearDistance, "Near")
            renderProps += this.toStr("farDistance", props.farDistance, "Far")
            renderProps += this.toStr("borderLineColor", props.borderLineColor, "BorderLineColor")
            renderProps += this.toStr("bBorderLine", props.bBorderLine, "VisualBorder")
            renderProps += this.toStr("renderDistance", props.renderDistance, "RenderDistance")
            renderProps += this.toStr("minTransparencyDistortion", props.minTransparencyDistortion, "MinTransparencyDistortion")
            renderProps += this.toStr("fadeOutDuration", props.fadeOutDuration, "FadeOutDuration")
            renderProps += this.toStr("edgeTransparency", props.edgeTransparency, "EdgeTransparentRatio")
            componetProps += renderProps + "#$%"
            categoryNames += renderCategoryName + "#$%"
            //stream componet props
            let streamProps = ""
            streamProps += this.toStr("serverUrl", props.serverUrl, "WsUrl")
            streamProps += this.toStr("videoUrl", props.videoUrl, "Url")
            streamProps += this.toStr("bBase64", props.bBase64, "IsBase64")
            componetProps += streamProps + "#$%"
            categoryNames += streamCategoryName + "#$%"
            //decode component props
            let decodeProps = ""
            decodeProps += "|"
            componetProps += decodeProps + "#$%"
            categoryNames += decodeCategoryName + "#$%"
            //altitude control component props
            let avcProps = ""
            avcProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
            avcProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")
            componetProps += avcProps + "#$%"
            categoryNames += avcCategoryName + "#$%"
            var idInfo = ""
            idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(categoryNames), encode(componetProps), false, this.bVisible, defined(callback))
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentRenderUuid = idArray[1]
                this.componentStreamUuid = idArray[2]
                this.componentDecodeUuid = idArray[3]
                this.componentAltitudeVisCtrlUuid = idArray[4]
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (state) {
                        callback(state)
                    }
                    Element.prototype.Map.videoFusions.push(this)
                })
                // console.log("VideoFusion avatarUuid:" + this.avatarUuid + " Render:" + this.componentRenderUuid + " Stream:" + this.componentStreamUuid + " Decode:" + this.componentDecodeUuid)
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object：
         * @param {String} [props.serverUrl] websocket服务地址。
         * @param {String} [props.videoUrl] 视频流地址。
         * @param {EnumVideoPlayMode} [props.playMode] 播放模式，当为AUTO时，playStatus不生效。
         * @param {Number} [props.autoPlayDistance] 最小自动播放距离。
         * @param {EnumVideoPlayStatus} [props.playStatus] 视频播放状态，当playMode为AUTO时不生效。
         * @param {Vec3Obj} [props.eye] 观察点坐标。
         * @param {Vec3Obj} [props.center] 目标点坐标。
         * @param {Vec3Obj} [props.up] 上方向。
         * @param {Number} [props.fovy] 视野角度。
         * @param {Number} [props.aspect] 纵横比。
         * @param {Number} [props.nearDistance] 近裁剪面距离。
         * @param {Number} [props.farDistance] 远裁剪面距离。
         * @param {Number} [props.fadeOutDuration] 淡出持续时间。
         * @param {Number} [props.edgeTransparency] 边缘透明系数，范围0-0.5。
         * @param {ColorObj} [props.borderLineColor] 轮廓线颜色。
         * @param {Boolean} [props.bBorderLine] 是否开启轮廓线。
         * @param {Number} [props.renderDistance] 绘制距离。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {Number} [props.minTransparencyDistortion] 最小透明度失真。
         * @param {Boolean} [props.bBase64] 是否为Base64编解码。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback] 加载成功后触发的函数回调。
         * @example
         * fusion.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            let uuids = ""
            if (defined(props)) {
                //avatar props
                if (defined(props.playMode)) {
                    this.playMode = props.playMode
                    avatarProps += "PlayMode:" + this.playMode + "|"
                }
                if (defined(props.autoPlayDistance)) {
                    this.autoPlayDistance = props.autoPlayDistance
                    avatarProps += "AutoPlayDistance:" + this.autoPlayDistance + "|"
                }
                if (defined(props.playStatus)) {
                    this.playStatus = props.playStatus
                    avatarProps += "ControlPlayStatus:" + this.playStatus + "|"
                }
                if (defined(props.bVisible)) {
                    this.bVisible = props.bVisible
                    avatarProps += "Visible:" + this.bVisible + "|"
                }
                //render componet props
                if (defined(props.eye)) {
                    this.eye = props.eye
                    componentProps += "Eye:" + this.eye.toStr() + "|"
                }
                if (defined(props.center)) {
                    this.center = props.center
                    componentProps += "Center:" + this.center.toStr() + "|"
                }
                if (defined(props.up)) {
                    this.up = props.up
                    componentProps += "Up:" + this.up.toStr() + "|"
                }
                if (defined(props.fovy)) {
                    this.fovy = props.fovy
                    componentProps += "Fovy:" + this.fovy + "|"
                }
                if (defined(props.aspect)) {
                    this.aspect = props.aspect
                    componentProps += "Aspect:" + this.aspect + "|"
                }
                if (defined(props.nearDistance)) {
                    this.nearDistance = props.nearDistance
                    componentProps += "Near:" + this.nearDistance + "|"
                }
                if (defined(props.farDistance)) {
                    this.farDistance = props.farDistance
                    componentProps += "Far:" + this.farDistance + "|"
                }
                if (defined(props.borderLineColor)) {
                    this.borderLineColor = props.borderLineColor
                    componentProps += "BorderLineColor:" + this.borderLineColor.toStr() + "|"
                }
                if (defined(props.bBorderLine)) {
                    this.bBorderLine = props.bBorderLine
                    componentProps += "VisualBorder:" + this.bBorderLine + "|"
                }
                if (defined(props.renderDistance)) {
                    this.renderDistance = props.renderDistance
                    componentProps += "RenderDistance:" + this.renderDistance + "|"
                }
                if (defined(props.minTransparencyDistortion)) {
                    this.minTransparencyDistortion = props.minTransparencyDistortion
                    componentProps += "MinTransparencyDistortion:" + this.minTransparencyDistortion + "|"
                }
                if (defined(props.fadeOutDuration)) {
                    this.fadeOutDuration = props.fadeOutDuration
                    componentProps += "FadeOutDuration:" + this.fadeOutDuration + "|"
                }
                if (defined(props.edgeTransparency)) {
                    this.edgeTransparency = props.edgeTransparency
                    componentProps += "EdgeTransparentRatio:" + this.edgeTransparency + "|"
                }
                componentProps += "#$%"
                uuids += this.componentRenderUuid + "#$%"
                //stream component props
                if (defined(props.serverUrl)) {
                    this.serverUrl = props.serverUrl
                    componentProps += "WsUrl:" + this.serverUrl + "|"
                }
                if (defined(props.videoUrl)) {
                    this.videoUrl = props.videoUrl
                    componentProps += "Url:" + this.videoUrl + "|"
                }
                if (defined(props.bBase64)) {
                    this.bBase64 = props.bBase64
                    componentProps += "IsBase64:" + this.bBase64 + "|"
                }
                componentProps += "#$%"
                uuids += this.componentStreamUuid + "#$%"
                //decode component props
                componentProps += "|"
                componentProps += "#$%"
                uuids += this.componentDecodeUuid + "#$%"
                //altitude control component props
                if (defined(props.minAltitude)) {
                    this.minAltitude = props.minAltitude
                    componentProps += "MinVisibleAltitude:" + this.minAltitude + "|"
                }
                if (defined(props.maxAltitude)) {
                    this.maxAltitude = props.maxAltitude
                    componentProps += "MaxVisibleAltitude:" + this.maxAltitude + "|"
                }
                componentProps += "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"

                callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * fusion.locate((state)=>{console.log("locate status:" + state)});
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * fusion.getProperties();
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                } else if (name == "PlayMode") {
                    this.playMode = Number(value)
                } else if (name == "PlayStatus") {
                    this.playStatus = Number(value)
                } else if (name == "AutoPlayDistance") {
                    this.autoPlayDistance = Number(value)
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                console.log("componentName:" + pName + " uuid:" + pUuid)
                if (pName == "FrustumMonitorRenderComponent") {
                    this.componentRenderUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "Eye") {
                            this.eye = Vec3Obj.fromStr(value)
                        } if (name == "Center") {
                            this.center = Vec3Obj.fromStr(value)
                        } if (name == "Up") {
                            this.up = Vec3Obj.fromStr(value)
                        } else if (name == "Fovy") {
                            this.fovy = Number(value)
                        } else if (name == "Aspect") {
                            this.aspect = Number(value)
                        } else if (name == "Near") {
                            this.nearDistance = Number(value)
                        } else if (name == "Far") {
                            this.farDistance = Number(value)
                        } else if (name == "BorderLineColor") {
                            this.borderLineColor = ColorObj.fromStr(value)
                        } else if (name == "VisualBorder") {
                            if (value == "true" || value == "1") {
                                this.bBorderLine = true
                            } else {
                                this.bBorderLine = false
                            }
                        } else if (name == "RenderDistance") {
                            this.renderDistance = Number(value)
                        } else if (name == "MinTransparencyDistortion") {
                            this.minTransparencyDistortion = Number(value)
                        } else if (name == "FadeOutDuration") {
                            this.fadeOutDuration = Number(value)
                        } else if (name == "EdgeTransparentRatio") {
                            this.edgeTransparency = Number(value)
                        }
                    }
                } else if (pName == "WebsocketComponent") {
                    this.componentStreamUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "WsUrl") {
                            this.serverUrl = value
                        } else if (name == "Url") {
                            this.videoUrl = value
                        } else if (name == "IsBase64") {
                            if (value == "true" || value == "1") {
                                this.bBase64 = true
                            } else {
                                this.bBase64 = false
                            }
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁后触发的函数回调。
         * @returns null 空值。
         * @example
         * fusion = fusion.destroy((state)=>{console.log("destroy status:" + state)});
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.videoFusions = Element.prototype.Map.videoFusions.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentDecodeUuid = null
                this.componentRenderUuid = null
                this.componentStreamUuid = null
                this.componentAltitudeVisCtrlUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return VideoFusion
})